// ============================================================================
// FIREBASE CLIENT - Conexión a la base de datos (Firestore)
// ============================================================================

import { initializeApp } from 'firebase/app';
import {
    getFirestore,
    collection,
    getDocs,
    addDoc,
    setDoc,
    updateDoc,
    doc,
    query,
    where,
    orderBy,
    limit as firestoreLimit,
    onSnapshot
} from 'firebase/firestore';
import { Character, Memory } from '../types/Character';
import { Conversation } from '../types/Conversation';
import { Zone } from '../types/Town';
import { Project, SoftwareTask } from '../types/Project';
import { initialCharacters, initialZones, initialProject, initialTasks } from '../data/initialData';

// Tipos para las tablas
export interface DBAgent {
    id: string;
    name: string;
    role: string;
    personality: string;
    skills: string[];
    communicationStyle: string;
    sprite: string;
    position_x: number;
    position_y: number;
    status: 'idle' | 'moving' | 'talking' | 'working' | 'reviewing';
    currentTask: string | null;
    last_action_at: string;
    version?: string;
    daily_schedule?: any; // JSON
}

export interface DBMemory {
    id: string;
    agent_id: string; // was character_id
    description: string;
    type: 'observation' | 'reflection' | 'plan' | 'conversation' | 'decision';
    importance: number;
    embedding?: number[];
    relatedAgents?: string[];
    relatedTask?: string | null;
    created_at: string;
}

export interface DBZone {
    id: string;
    name: string;
    type: 'meeting_room' | 'open_office' | 'review_corner' | 'cafeteria';
    purpose: string;
    triggers: string;
    position_x: number;
    position_y: number;
    width: number;
    height: number;
}

export interface DBConversation {
    id: string;
    character1_id: string;
    character2_id: string;
    messages: any[]; // JSON
    location: string;
    topic: string;
    started_at: string;
    ended_at: string | null;
}

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app, 'virtual-software-house');

// ============================================================================
// AUTO SEED
// ============================================================================

export async function autoSeedDatabase(): Promise<void> {
    try {
        const q = query(collection(db, 'agents'), firestoreLimit(1));
        const snap = await getDocs(q);
        if (!snap.empty) {
            console.log("Database already seeded. Skipping auto-seed.");
            return;
        }

        console.log("Seeding Database for The Virtual Software House...");

        // 1. Seed Zones
        for (const zone of initialZones) {
            const dbZone: DBZone = {
                id: zone.id,
                name: zone.name,
                type: zone.type,
                purpose: zone.purpose,
                triggers: zone.triggers,
                position_x: zone.position.x,
                position_y: zone.position.y,
                width: zone.position.width,
                height: zone.position.height
            };
            await setDoc(doc(db, 'zones', zone.id), dbZone);
        }

        // 2. Seed Projects and Tasks
        await setDoc(doc(db, 'projects', initialProject.id), {
            name: initialProject.name,
            description: initialProject.description,
            status: initialProject.status,
            createdAt: initialProject.createdAt.toISOString()
        });

        for (const task of initialTasks) {
            await setDoc(doc(db, `projects/${initialProject.id}/tasks`, task.id), {
                title: task.title,
                description: task.description,
                assignedTo: task.assignedTo,
                status: task.status,
                priority: task.priority,
                dependencies: task.dependencies,
                createdBy: task.createdBy,
                history: task.history,
                createdAt: task.createdAt.toISOString()
            });
        }

        // 3. Seed Agents and Memories
        for (const agent of initialCharacters) {
            const dbAgent: DBAgent = {
                id: agent.id,
                name: agent.name,
                role: agent.role,
                personality: agent.personality,
                skills: agent.skills,
                communicationStyle: agent.communicationStyle,
                sprite: agent.sprite,
                position_x: agent.position.x,
                position_y: agent.position.y,
                status: agent.status,
                currentTask: agent.currentTask,
                last_action_at: agent.lastTaskCompletedAt.toISOString(),
                version: agent.version,
                daily_schedule: agent.dailySchedule
            };
            await setDoc(doc(db, 'agents', agent.id), dbAgent);

            // Seed Memories as a subcollection
            for (const mem of agent.memories) {
                const memData: Omit<DBMemory, 'id'> = {
                    agent_id: agent.id,
                    description: mem.description,
                    type: mem.type,
                    importance: mem.importance,
                    embedding: mem.embedding || [],
                    relatedAgents: mem.relatedAgents || [],
                    relatedTask: mem.relatedTask || null,
                    created_at: mem.timestamp.toISOString()
                };
                await addDoc(collection(db, `agents/${agent.id}/memories`), memData);
            }
        }
        
        console.log("✅ Auto-seed completed successfully!");
    } catch (error) {
        console.error("❌ Auto-seed failed:", error);
    }
}

// ============================================================================
// FUNCIONES DE LECTURA
// ============================================================================

export async function fetchCharacters(): Promise<DBAgent[]> {
    try {
        const q = query(collection(db, 'agents'), orderBy('name'));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DBAgent));
    } catch (error) {
        console.error('❌ Error fetching agents:', error);
        return [];
    }
}

export async function fetchZones(): Promise<DBZone[]> {
    try {
        const querySnapshot = await getDocs(collection(db, 'zones'));
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DBZone));
    } catch (error) {
        console.error('❌ Error fetching zones:', error);
        return [];
    }
}

export async function fetchMemories(agentId: string, max: number = 10): Promise<DBMemory[]> {
    try {
        const q = query(
            collection(db, `agents/${agentId}/memories`),
            orderBy('created_at', 'desc'),
            firestoreLimit(max)
        );
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DBMemory));
    } catch (error) {
        console.error('❌ Error fetching memories:', error);
        return [];
    }
}

export async function fetchConversations(max: number = 5): Promise<DBConversation[]> {
    try {
        const q = query(
            collection(db, 'conversations'),
            orderBy('started_at', 'desc'),
            firestoreLimit(max)
        );
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DBConversation));
    } catch (error) {
        console.error('❌ Error fetching conversations:', error);
        return [];
    }
}

// ============================================================================
// FUNCIONES DE ESCRITURA
// ============================================================================

export async function updateCharacterPosition(agentId: string, x: number, y: number): Promise<boolean> {
    try {
        const charRef = doc(db, 'agents', agentId);
        await updateDoc(charRef, {
            position_x: x,
            position_y: y,
            status: 'moving',
            last_action_at: new Date().toISOString()
        });
        return true;
    } catch (error) {
        console.error('❌ Error updating position:', error);
        return false;
    }
}

export async function updateCharacterStatus(agentId: string, status: DBAgent['status']): Promise<boolean> {
    try {
        const charRef = doc(db, 'agents', agentId);
        await updateDoc(charRef, { status });
        return true;
    } catch (error) {
        console.error('❌ Error updating status:', error);
        return false;
    }
}

export async function addMemory(
    agentId: string,
    description: string,
    type: DBMemory['type'] = 'observation',
    importance: number = 5,
    embedding?: number[]
): Promise<string | null> {
    try {
        const docRef = await addDoc(collection(db, `agents/${agentId}/memories`), {
            agent_id: agentId,
            description,
            type,
            importance,
            ...(embedding ? { embedding } : {}),
            created_at: new Date().toISOString()
        });
        return docRef.id;
    } catch (error) {
        console.error('❌ Error adding memory:', error);
        return null;
    }
}

// ============================================================================
// CONVERSIÓN DE TIPOS
// ============================================================================

export function dbCharacterToCharacter(dbChar: DBAgent): Character {
    return {
        id: dbChar.id,
        name: dbChar.name,
        role: dbChar.role,
        personality: dbChar.personality,
        skills: dbChar.skills || [],
        communicationStyle: dbChar.communicationStyle,
        sprite: dbChar.sprite,
        position: { x: dbChar.position_x, y: dbChar.position_y },
        memories: [], // Needs explicit fetch
        currentTask: dbChar.currentTask,
        dailySchedule: Array.isArray(dbChar.daily_schedule) ? dbChar.daily_schedule : [],
        status: dbChar.status,
        lastTaskCompletedAt: new Date(dbChar.last_action_at),
        version: dbChar.version,
    };
}

export function dbBuildingToBuilding(dbZone: DBZone): Zone {
    return {
        id: dbZone.id,
        type: dbZone.type,
        name: dbZone.name,
        purpose: dbZone.purpose,
        triggers: dbZone.triggers,
        position: {
            x: dbZone.position_x,
            y: dbZone.position_y,
            width: dbZone.width,
            height: dbZone.height,
        },
    };
}

// REALTIME SUBSCRIPTIONS
export function subscribeToCharacters(onUpdate: (characters: DBAgent[]) => void): (() => void) {
    const q = query(collection(db, 'agents'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
        const characters = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DBAgent));
        onUpdate(characters);
    });
    return unsubscribe;
}

export function subscribeToConversations(onUpdate: (conversations: DBConversation[]) => void): (() => void) {
    const q = query(collection(db, 'conversations'), orderBy('started_at', 'desc'), firestoreLimit(5));
    const unsubscribe = onSnapshot(q, (snapshot) => {
        const conversations = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DBConversation));
        onUpdate(conversations);
    });
    return unsubscribe;
}

export function dbConversationToConversation(dbConv: DBConversation, characters: Character[]): Conversation {
    return {
        id: dbConv.id,
        participants: [dbConv.character1_id, dbConv.character2_id].filter(Boolean),
        messages: Array.isArray(dbConv.messages) ? dbConv.messages : [],
        location: dbConv.location,
        timestamp: new Date(dbConv.started_at)
    };
}

export async function triggerGameTick(): Promise<{success: boolean, error?: string}> {
    const cloudRunUrl = process.env.REACT_APP_CLOUD_RUN_URL;

    // ── Production: call Cloud Run (Gemini key stays server-side) ─────────────
    if (cloudRunUrl) {
        try {
            const res = await fetch(`${cloudRunUrl}/tick`, { method: 'POST' });
            if (!res.ok) throw new Error(`Server responded ${res.status}`);
            const result = await res.json();
            return { success: result.success, error: result.error };
        } catch (error: any) {
            console.error('❌ Cloud Run tick failed:', error);
            return { success: false, error: error.message };
        }
    }

    // ── Development fallback: run game loop locally (key from .env) ────────────
    try {
        const { executeGameTick } = await import('./gameLoopService');
        const result = await executeGameTick();
        return { success: result.success, error: result.error };
    } catch (error: any) {
        console.error('❌ triggerGameTick failed:', error);
        return { success: false, error: error.message };
    }
}
